import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Chip, SectionCard, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CategoryView, ProductVariantView, ProductView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { getBuyerAbsoluteUrl, getSellerPath } from "@/lib/routing/portal-urls";
import { pickLocalized } from "@/lib/i18n/localized";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import {
  NavigationGuardProvider,
  useNavigationGuard,
} from "@/lib/navigation/navigationGuard";
import { ProductDetailsForm } from "./ProductDetailsForm";
import { ProductContentForm } from "./ProductContentForm";
import { ProductVariantsCard } from "./ProductVariantsCard";
import { ProductImagesForm } from "./ProductImagesForm";
import { ProductEditSidebar } from "./ProductEditSidebar";
import { ProductEditSaveBar } from "./ProductEditSaveBar";
import { CameraIcon } from "./product-edit-icons";
import { useVariantCostsQuery } from "@/modules/products/services/useVariantCostsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/core/query-keys";

/**
 * Trioty card chrome applied to the edit section wrappers on mobile (≤lg) only.
 * `!` beats the SectionCard `flat` base (surface bg / border-light / radius-20);
 * `max-lg:` keeps the desktop cards byte-for-byte unchanged.
 */
export const MOBILE_CARD_CHROME =
  "max-lg:!rounded-[18px] max-lg:!border-[color:var(--hair)] max-lg:!bg-[color:var(--soft)]";

type Props = {
  locale: Locale;
  storeSlug: string;
  product: ProductView;
  categories: CategoryView[];
  maxProductImageCount: number;
  /**
   * Called whenever the aggregated dirty state of any section changes.
   * Used by the hero to render an "Unsaved changes" chip.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Mobile sticky-header back: plays the reverse slide-out then navigates to
   *  the product detail view. Falls back to a plain list link when absent. */
  onBack?: () => void;
};

export function ProductEditSections(props: Props) {
  return (
    <NavigationGuardProvider>
      <ProductEditSectionsContent {...props} />
    </NavigationGuardProvider>
  );
}

function ProductEditSectionsContent({
  locale,
  storeSlug,
  product,
  categories,
  maxProductImageCount,
  onDirtyChange,
  onBack,
}: Props) {
  const t = useTranslations();
  const navigate = useNavigate();
  const guard = useNavigationGuard();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [photosDirty, setPhotosDirty] = useState(false);
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [contentDirty, setContentDirty] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  // Bumped on Discard to remount the section forms so they revert to the saved
  // values (no navigation away).
  const [resetKey, setResetKey] = useState(0);
  // One save click submits several forms; this guards a single success toast.
  const savedToastRef = useRef(false);

  const anyDirty = photosDirty || detailsDirty || contentDirty;
  const unsavedCount =
    (photosDirty ? 1 : 0) +
    (detailsDirty ? 1 : 0) +
    (contentDirty ? 1 : 0);

  // Ref to the main column so the SaveBar can scope `requestSubmit` calls
  // to the product-edit forms only (not any unrelated forms on the page).
  const mainRef = useRef<HTMLElement>(null);

  const handleSaveAll = useCallback(() => {
    const root = mainRef.current;
    if (!root) return;
    const forms = root.querySelectorAll<HTMLFormElement>("form");
    if (forms.length === 0) return;
    savedToastRef.current = false; // new save batch → allow one toast
    setIsSavingAll(true);
    forms.forEach((form) => {
      form.requestSubmit();
    });
    // Each form's own submit handler clears its dirty flag on success;
    // we drop the local "saving" pulse after a short delay so the bar
    // reflects the new state instead of hanging if a form errors silently.
    window.setTimeout(() => setIsSavingAll(false), 800);
  }, []);

  // Called by each section form on a successful save. Saving happens in place
  // (no navigation) and only the first form to finish shows the toast.
  const handleSectionSaved = useCallback(() => {
    // Cost lives in a separate seller-only query; refresh it so the re-seeded
    // "Cost per item" value reflects what was just saved.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.seller.productVariantCosts(storeSlug, product.id),
    });
    if (savedToastRef.current) return;
    savedToastRef.current = true;
    toast.success(t(keys.products.editSaved));
  }, [toast, t, queryClient, storeSlug, product.id]);

  // Discard = revert to the last saved state without leaving the page: remount
  // the section forms (they re-seed from the product) and clear dirty flags.
  const handleDiscard = useCallback(() => {
    setResetKey((k) => k + 1);
    setPhotosDirty(false);
    setDetailsDirty(false);
    setContentDirty(false);
    guard?.setDirtySource("images", false);
    guard?.setDirtySource("details", false);
    guard?.setDirtySource("content", false);
  }, [guard]);
  useEffect(() => {
    onDirtyChange?.(anyDirty);
  }, [anyDirty, onDirtyChange]);

  // Stable onDirtyChange callbacks — avoid creating new function references
  // each render, which would refire every child form's onDirtyChange effect
  // on every parent render (infinite loop risk now that ProductEditSections
  // subscribes to the lifted useForm).
  const handlePhotosDirtyChange = useCallback(
    (isDirty: boolean) => {
      setPhotosDirty(isDirty);
      guard?.setDirtySource("images", isDirty);
    },
    [guard],
  );
  const handleDetailsDirtyChange = useCallback(
    (isDirty: boolean) => {
      setDetailsDirty(isDirty);
      guard?.setDirtySource("details", isDirty);
    },
    [guard],
  );
  const handleContentDirtyChange = useCallback(
    (isDirty: boolean) => {
      setContentDirty(isDirty);
      guard?.setDirtySource("content", isDirty);
    },
    [guard],
  );
  const handleNavigateToList = useCallback(
    () => navigate(`${getSellerPath(locale, storeSlug)}/products`),
    [locale, navigate, storeSlug],
  );

  const defaultVariant: ProductVariantView | null = useMemo(
    () =>
      product.variants.find((variant) => variant.id === product.defaultVariantId) ??
      product.variants.find((variant) => variant.isDefault) ??
      product.variants[0] ??
      null,
    [product.defaultVariantId, product.variants],
  );

  // Seller-only goods cost for the default variant — fetched apart from the
  // buyer-shared product view. Seeds the product-level "Cost per item" field for
  // options-less products (the only ones with product-level pricing).
  const { data: variantCosts } = useVariantCostsQuery({
    storeSlug,
    productId: product.id,
  });
  const defaultVariantCost = useMemo(() => {
    if (!defaultVariant || !variantCosts) return null;
    return (
      variantCosts.find((entry) => entry.variantId === defaultVariant.id)
        ?.cost ?? null
    );
  }, [variantCosts, defaultVariant]);

  const detailsDefaultValues = useMemo(
    () => ({
      nameAr: product.nameAr,
      nameEn: product.nameEn,
      shortDescriptionAr: product.shortDescriptionAr,
      shortDescriptionEn: product.shortDescriptionEn,
      categoryIds: product.categoryIds,
      price: defaultVariant?.price ?? 0,
      discountedPrice: defaultVariant?.discountedPrice ?? null,
      cost: defaultVariantCost,
      stockCount: defaultVariant?.stockCount ?? 0,
      forceOutOfStock: defaultVariant?.forceOutOfStock ?? false,
      isActive: product.isActive,
    }),
    [
      product.categoryIds,
      product.shortDescriptionAr,
      product.shortDescriptionEn,
      defaultVariant?.discountedPrice,
      defaultVariantCost,
      defaultVariant?.forceOutOfStock,
      product.isActive,
      product.nameAr,
      product.nameEn,
      defaultVariant?.price,
      defaultVariant?.stockCount,
    ],
  );


  const contentDefaultValues = useMemo(
    () => ({
      shortDescriptionAr: product.shortDescriptionAr,
      shortDescriptionEn: product.shortDescriptionEn,
      highlights: product.highlights,
      details: product.details,
      faq: product.faq,
      notes: product.notes,
    }),
    [
      product.details,
      product.faq,
      product.highlights,
      product.notes,
      product.shortDescriptionAr,
      product.shortDescriptionEn,
    ],
  );

  const productImages = useMemo(() => product.images, [product.images]);

  // Hash-based auto-scroll: when URL hash matches a known section id,
  // smooth-scroll to it on mount. Sections no longer collapse — they're
  // always expanded in the redesign — but deep links still work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (!["photos", "details", "description", "options"].includes(hash)) return;
    const timer = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Sticky mobile header data. On mobile the PageChrome hero/summary card are
  // dropped (ProductEditPage nulls them) and the header lives here instead, so
  // it pins to the top of the scroll container while the cards scroll under it.
  const productsHref = `${getSellerPath(locale, storeSlug)}/products`;
  const headerProductName = pickLocalized(locale, product.nameAr, product.nameEn);
  const previewHref = product.productRef
    ? getBuyerAbsoluteUrl(
        locale,
        storeSlug,
        `/products/${encodeURIComponent(product.productRef)}`,
      )
    : null;

  return (
    <div className="flex w-full flex-col max-lg:bg-[color:var(--canvas)]">
    <div className="grid w-full grid-cols-1 gap-3.5 px-4 py-4 lg:gap-5 lg:px-7 lg:py-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <main ref={mainRef} className="flex min-w-0 flex-col gap-3.5 lg:gap-5">
        {/* Mobile-only sticky header. Full-bleed (`-mx-4` cancels the grid's
            px-4; `-mt-4` cancels its py-4 so it sits flush at the top) and pinned
            to the scroll container top while cards scroll under it. On desktop the
            PageChrome breadcrumb/summary card handle this, so it renders nothing. */}
        {isMobile ? (
          <div
            className="sticky top-0 z-30 -mx-4 -mt-4 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
            style={{
              paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
              paddingBottom: 12,
            }}
          >
            <div className="flex items-center gap-3">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label={t(keys.common.back)}
                  className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
                >
                  <HeaderBackIcon />
                </button>
              ) : (
                <Link
                  to={productsHref}
                  aria-label={t(keys.common.back)}
                  className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
                >
                  <HeaderBackIcon />
                </Link>
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <h1 className="m-0 truncate text-[18px] font-extrabold leading-[1.15] tracking-[-0.02em] text-[color:var(--text-primary)]">
                  {t(keys.seller.productsEditTitle)}
                </h1>
                {headerProductName ? (
                  <span className="mt-0.5 truncate text-[12px] font-semibold text-[color:var(--text-muted)]">
                    {headerProductName}
                  </span>
                ) : null}
              </div>
              {anyDirty ? (
                <Chip
                  tone="accent"
                  size="sm"
                  className="shrink-0 !border-[color-mix(in_oklab,var(--accent-500)_35%,transparent)] !bg-[color-mix(in_oklab,var(--accent-500)_15%,transparent)] !text-[color:var(--accent-500)]"
                  leadingIcon={
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--accent-500)]" />
                  }
                >
                  {t(keys.common.unsavedChanges)}
                </Chip>
              ) : null}
            </div>
          </div>
        ) : null}

        <SectionCard
          id="photos"
          variant="flat"
          // On mobile the photo panel renders its own header (icon + "Photos" +
          // N/max count) to match the design, so hide the SectionCard header to
          // avoid a duplicate title and restore the body's top padding.
          className={`${MOBILE_CARD_CHROME} max-lg:[&>header]:hidden max-lg:[&>div]:pt-[15px]`}
          title={t(keys.products.imagesTitle)}
          subtitle={t(keys.products.editPhotosSubtitle)}
          icon={<CameraIcon />}
          isDirty={photosDirty}
        >
          <ProductImagesForm
            key={`photos-${resetKey}`}
            locale={locale}
            storeSlug={storeSlug}
            productId={product.id}
            onCancel={handleNavigateToList}
            onSuccess={handleSectionSaved}
            initialImages={productImages}
            redirectOnSuccess={false}
            showTips
            maxImages={maxProductImageCount}
            enableNavigationGuard={false}
            onDirtyChange={handlePhotosDirtyChange}
            hideActions
          />
        </SectionCard>

        {/* Mobile only: the right-rail Publishing + Inventory content is surfaced
            inline as card #2 (right after Photos). The desktop rail below is
            hidden on mobile so this is the single live instance there. The
            sidebar owns no <form>, so mainRef's save-all `querySelectorAll("form")`
            never picks it up — its instant-save handlers stay independent. */}
        <div className="flex flex-col gap-3.5 lg:hidden">
          <ProductEditSidebar
            storeSlug={storeSlug}
            product={product}
            defaultVariant={defaultVariant}
          />
        </div>

        {/* ProductDetailsForm renders Product details + Pricing as two
            SectionCards. The "Rich content" tab in the Product details
            header swaps the body to ProductContentForm (its own internal
            form for highlights/details/FAQ/notes). */}
        <ProductDetailsForm
          key={`details-${resetKey}`}
          locale={locale}
          storeSlug={storeSlug}
          mode="edit"
          productId={product.id}
          categories={categories}
          onCancel={handleNavigateToList}
          onSuccess={handleSectionSaved}
          enableNavigationGuard={false}
          onDirtyChange={handleDetailsDirtyChange}
          defaultValues={detailsDefaultValues}
          hasVariants={product.optionGroups.length > 0}
          hideActions
          richContent={
            <ProductContentForm
              key={`content-${resetKey}`}
              locale={locale}
              storeSlug={storeSlug}
              productId={product.id}
              onCancel={handleNavigateToList}
              onSuccess={handleSectionSaved}
              enableNavigationGuard={false}
              onDirtyChange={handleContentDirtyChange}
              defaultValues={contentDefaultValues}
              hideActions
            />
          }
        />

        <ProductVariantsCard storeSlug={storeSlug} product={product} />

        {/* TODO(new-soon): Shipping section (weight / length / width / origin).
            Hidden until the backend persists per-product shipping dimensions.
            Original implementation: see git history before 2026-05-30. */}

        {/* TODO(new-soon): SEO section (URL handle, meta title, Google preview).
            Hidden until the backend exposes editable seoMetaTitle / slug fields.
            The slug currently displayed on the buyer storefront comes from
            `product.slug` and is not editable here. Original implementation:
            see git history before 2026-05-30. */}
      </main>

      {/* Desktop right rail (lg+). Hidden on mobile, where the same content is
          rendered inline above as card #2. Byte-for-byte unchanged at lg+. */}
      <div className="hidden lg:block xl:sticky xl:top-4 xl:self-start">
        <ProductEditSidebar
          storeSlug={storeSlug}
          product={product}
          defaultVariant={defaultVariant}
        />
      </div>
    </div>
    <ProductEditSaveBar
      unsavedCount={unsavedCount}
      lastEditedLabel={null}
      isSaving={isSavingAll}
      onDiscard={handleDiscard}
      onSaveChanges={handleSaveAll}
      previewHref={previewHref}
      labels={{
        discard: t(keys.products.editSaveBarDiscard),
        saveChanges: t(keys.products.editSaveBarSaveChanges),
        unsavedSingular: t(keys.products.editSaveBarUnsavedOne),
        unsavedPlural: t(keys.products.editSaveBarUnsavedMany),
        preview: t(keys.products.editSummaryPreview),
        saved: t(keys.products.editSaveBarSaved),
      }}
    />
    </div>
  );
}

function HeaderBackIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:-scale-x-100"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

